import { Check, ChevronDown, History, Lock, LockOpen, Save, Send, Undo2 } from 'lucide-react';
import * as React from 'react';
import {
  chainIncludes,
  RATING_STAGES,
  formatPlainDate,
  incompleteKraAnswers,
  KRA_RATING_OPTIONS,
  KRA_RATING_STAGES,
  meanRating,
  nextStage,
  ratesKras,
  STAGE_LABELS,
  toGrade,
  weightedOverall,
  type AssessmentStage,
} from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import {
  EvidenceField,
  EvidencePanel,
} from '@/components/attachments/evidence-panel';
import { ProgressEntry, RichNote, RichNoteEditor } from '@/components/rich-note';
import { useAttachments } from '@/lib/attachments-api';
import { StageTracker } from '@/components/assessments/stage-tracker';
import { DevConnectCard } from '@/components/assessments/dev-connect-card';
import { PlainDatePicker } from '@/components/plain-date-picker';
import {
  ASSESSMENT_EXCEPTION_LABELS,
  assessmentExceptionVariant,
  assessmentStatusVariant,
  gradeVariant,
  PILL_CLASS,
} from '@/lib/badges';
import {
  useAcceptRating,
  useAssessment,
  useDeclineRating,
  useReopenAssessment,
  useReturnForCorrection,
  useSaveReviewDraft,
  useSaveSelf,
  useSubmitStage,
  type Assessment,
  type AssessmentKra,
  type KraRatingInput,
} from '@/lib/assessments-api';
import { useGoalHeading } from '@/lib/goals-api';
import { cn } from '@/lib/utils';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

/** For the collapsed KRA row, where the full stage label would not fit. */
const SHORT_STAGE: Record<AssessmentStage, string> = {
  SELF: 'Self',
  MANAGER: 'Manager',
  EMPLOYEE_ACCEPTANCE: 'You',
  HR: 'HR',
  MANAGEMENT: 'Mgmt',
};

/**
 * Per-KRA rating: the five bands from `KRA_RATING_OPTIONS`, at every stage.
 *
 * Every stage uses the same list on purpose. The detail view puts the self and
 * manager ratings side by side so the gap is visible, and a gap between a picked
 * band and a typed 7.5 is not a gap anyone can read.
 */
function KraRatingSelect({
  value,
  onChange,
  disabled,
  label,
}: {
  value: number | '';
  onChange: (v: number) => void;
  disabled?: boolean;
  label: string;
}) {
  // A rating typed in before this was a dropdown still has to show and stay editable.
  // A select that cannot represent it would render blank, and the draft would look
  // unanswered rather than answered off-scale.
  const offScale = typeof value === 'number' && !KRA_RATING_OPTIONS.some((o) => o.value === value);
  return (
    <Select
      // '' rather than undefined: undefined would hand the select back to Radix as
      // uncontrolled, and it would then keep a stale pick of its own.
      value={value === '' ? '' : String(value)}
      disabled={disabled}
      onValueChange={(v) => onChange(Number(v))}
    >
      <SelectTrigger className="h-9 w-full shrink-0 sm:w-[280px]" aria-label={label}>
        <SelectValue placeholder="Select rating" />
      </SelectTrigger>
      <SelectContent>
        {offScale && (
          <SelectItem value={String(value)}>{value} — recorded before the scale</SelectItem>
        )}
        {KRA_RATING_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={String(option.value)}>
            {option.label} ({option.grade})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * One stage's answer on one KRA: its band, its reason, and (for the employee) the
 * evidence behind it.
 *
 * `editable` is the whole ownership rule, and it is deliberately a single condition
 * rather than one per control: a stage's column is writable only by whoever is
 * standing at that stage right now. An employee never has `activeStage === 'MANAGER'`
 * and a manager never has `'SELF'`, so neither can reach into the other's box. The
 * API enforces the same thing independently - every write lands on the row keyed
 * `(assessment, kra, stage)` for the stage the actor was authorized at - so a
 * hand-rolled request cannot overwrite the other side either.
 */
/**
 * The band this stage starts on: its own, if it has one. Never seeded from an earlier
 * stage - the manager answers the employee's claim, and starting them on the
 * self-rating would anchor every review to whatever the employee gave themselves.
 */
function seedRating(kra: AssessmentKra, stage: AssessmentStage | null): number | '' {
  return kra.ratings.find((r) => r.stage === stage)?.rating ?? '';
}

function KraStageColumn({
  kra,
  stage,
  editable,
  rating,
  comment,
  onRating,
  onComment,
  onCommentBlur,
  onSaveDraft,
  savePending,
}: {
  kra: AssessmentKra;
  stage: AssessmentStage;
  editable: boolean;
  rating: number | '';
  comment: string;
  onRating: (v: number) => void;
  onComment: (v: string) => void;
  /** Fired on blur: persists the note so evidence has a row to attach to. */
  onCommentBlur?: () => void;
  onSaveDraft?: () => void;
  savePending?: boolean;
}) {
  const recorded = kra.ratings.find((r) => r.stage === stage) ?? null;

  /**
   * A reviewer answers a self-rating, so their controls wait for one to exist. The
   * chain already guarantees it - a self-assessment cannot be submitted with a KRA
   * unrated - but the column should say what it is waiting for rather than offer a
   * dropdown over an empty claim.
   */
  const awaitingSelf = stage !== 'SELF' && !kra.ratings.some((r) => r.stage === 'SELF');
  const canAnswer = editable && !awaitingSelf;

  /**
   * The comment and the evidence are open from the start now, not gated behind a band.
   *
   * They used to wait for a rating, which made sense while a rating row could not exist
   * without one. It no longer can't: the point of a quarter is to log the proof as the
   * work happens - the BRD you shipped in June, the sign-off mail - months before
   * deciding what the quarter was worth. Forcing a band first meant either picking one
   * in week two and forgetting to revisit it, or keeping the evidence somewhere else.
   *
   * Read-only columns are never gated either - a reviewer has to see what was written.
   */
  const showDetail = true;

  /**
   * Evidence hangs off this stage's own rating row. That row is created by the first
   * autosave, which now fires on a comment as well as on a band, so it exists as soon
   * as anything is written.
   *
   * Every stage, not just SELF. It used to be the employee's alone, on the reasoning
   * that a manager has nothing of their own to file about someone else's work - but
   * they do: the client mail that contradicts the self-rating, the incident report, the
   * peer note. The API has always allowed it (`assertCanWrite` accepts the reporting
   * manager and HR); only this screen withheld the box, so the material ended up in
   * email where the next reviewer never saw it.
   *
   * Upload is still `editable`, so each stage attaches to its own row and nobody adds
   * to anybody else's. The employee's own row locks on submission, in the API.
   */
  const evidenceId = recorded?.id ?? null;

  /**
   * The comment box, which is also the evidence drop target once a row exists. From
   * then on `EvidenceField` draws the box around it and the attach bar, so this gives
   * up its own chrome.
   */
  const detail = editable ? (
    // The same editor as a goal's progress box, so a comment formats the same way.
    // The wrapper owns the border only until EvidenceField draws the box itself.
    <div
      data-guide="kra-comment"
      className={cn(
        !evidenceId &&
          'rounded-md border border-input bg-background shadow-sm focus-within:ring-1 focus-within:ring-ring',
      )}
      // Saves on blur, not on every keystroke. It is what creates the row the
      // paperclip hangs off, so a note written in week two has somewhere to
      // attach its evidence without anyone pressing Save first. Focus moving to
      // the toolbar is not leaving the box.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onCommentBlur?.();
      }}
    >
      <RichNoteEditor
        value={comment}
        onChange={onComment}
        label={`Your comment on ${kra.name}`}
        placeholder={
          stage === 'SELF'
            ? 'Required — what you did, and the evidence for it'
            : 'Required — ask about their reasoning, or say what you saw differently'
        }
      />
    </div>
  ) : recorded ? (
    // Answered already. No comment means none was written, not one pending, so
    // an empty box here would invite a reader to wait for something.
    recorded.comment && <RichNote text={recorded.comment} className="text-sm text-muted-foreground" />
  ) : (
    <Textarea
      className="min-h-20"
      placeholder={
        stage === 'SELF'
          ? 'What you did, and the evidence for it'
          : 'Ask about their reasoning, or say what you saw differently'
      }
      aria-label={`${STAGE_LABELS[stage]} comment on ${kra.name}`}
      // readOnly as well as disabled: a controlled value with no onChange is
      // a React warning, and disabled alone does not satisfy it.
      value=""
      readOnly
      disabled
    />
  );

  return (
    <div className={cn('rounded-md border p-3', editable ? 'bg-background' : 'bg-muted/40')}>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {STAGE_LABELS[stage]}
      </p>

      {canAnswer ? (
        <KraRatingSelect
          label={`Your rating for ${kra.name}`}
          value={rating}
          onChange={onRating}
        />
      ) : recorded && recorded.rating !== null ? (
        <p className="text-sm">
          <span className="font-semibold">{toGrade(recorded.rating)}</span>
          {/* The number stays beside the letter: the overall rating is a weighted
              mean of these, and it has to be traceable. */}
          <span className="pms-num text-muted-foreground"> ({recorded.rating})</span>
        </p>
      ) : (
        /*
         * The same control the active stage gets, disabled - not a line of text in
         * its place. Two columns side by side, one a form and one a sentence, read
         * as "nothing ever happens on that side"; the greyed copy reads as "not
         * yet", which is what it means.
         */
        <>
          <KraRatingSelect
            label={`${STAGE_LABELS[stage]} rating for ${kra.name}`}
            value=""
            onChange={() => {}}
            disabled
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {/* A row with a comment and no band is work in progress, never a zero -
                `toGrade(null)` would print D and read as the lowest possible mark. */}
            {recorded
              ? 'Notes logged, no band chosen yet'
              : awaitingSelf
                ? 'Waiting for the self rating'
                : 'Not rated yet'}
          </p>
        </>
      )}

      {showDetail &&
        (evidenceId ? (
          // The paperclip lives in the comment box and the box is the drop target,
          // so evidence costs one icon instead of a dropzone per stage per KRA.
          <div className="mt-2">
            <EvidenceField entityType="KRA_RATING" entityId={evidenceId} canUpload={editable}>
              {detail}
            </EvidenceField>
          </div>
        ) : (
          <div className="mt-2">{detail}</div>
        ))}

      {/* Evidence hangs off the rating row, and that row is created by the first
          autosave. Typing a comment or picking a band saves on its own, so this state
          lasts a keystroke - the button is the way back if that save failed. */}
      {editable && !evidenceId && onSaveDraft && (
        <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
          {savePending ? (
            'Saving…'
          ) : (
            <>
              <Button hint={"Keep your answers without submitting — you can come back to them"} variant="link" className="h-auto p-0 text-xs" onClick={onSaveDraft}>
                Save draft
              </Button>{' '}
              to attach evidence here.
            </>
          )}
        </p>
      )}
    </div>
  );
}

/** 0-10 rating input, for the final and potential ratings a weighted mean feeds. */
function RatingInput({
  value,
  onChange,
  disabled,
  label,
}: {
  value: number | '';
  onChange: (v: number | '') => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <Input
      type="number"
      min={0}
      max={10}
      step={0.5}
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      className="h-9 w-20 text-right"
    />
  );
}

/**
 * What was logged on My goals against this goal, read-only, under its heading - so the
 * self comment can summarise it instead of retyping it, and the reviewer reads the
 * quarter's record beside the rating. Collapsed: it is reference, not the task.
 */
function GoalProgressLog({ goalId, updates }: { goalId: string; updates: Assessment['goalUpdates'] }) {
  const { data: files } = useAttachments('GOAL', goalId);
  const fileCount = files?.length ?? 0;
  if (updates.length === 0 && fileCount === 0) return null;

  return (
    <Collapsible className="rounded-md border border-dashed bg-muted/30">
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/50 [&[data-state=open]_.chevron]:rotate-180">
        <History className="size-3.5 shrink-0" />
        <span className="flex-1">
          Earlier progress updates
          {updates.length > 0 && ` · ${updates.length} update${updates.length === 1 ? '' : 's'}`}
          {fileCount > 0 && ` · ${fileCount} file${fileCount === 1 ? '' : 's'}`}
        </span>
        <ChevronDown className="chevron size-3.5 shrink-0 transition-transform" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 px-3 pb-3">
        {updates.map((u) => (
          <ProgressEntry key={u.id} update={u} />
        ))}
        {fileCount > 0 && (
          <EvidenceField entityType="GOAL" entityId={goalId} canUpload={false}>
            {null}
          </EvidenceField>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * One assessment, in whichever mode the viewer is entitled to.
 *
 * Mode comes from the server (canEditSelf / myPendingStage) rather than being
 * inferred from the role here - the same reason the goal set does it: two copies of
 * that logic drift apart, and this one decides who can write a rating.
 */
export function AssessmentDetail({
  employeeId,
  quarterId,
}: {
  employeeId: string | 'me';
  quarterId: string;
}) {
  const { data: assessment, isPending, error } = useAssessment(employeeId, quarterId);

  if (isPending) return <Skeleton className="h-96 w-full" />;
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return <Loaded assessment={assessment} employeeId={employeeId} quarterId={quarterId} />;
}

function Loaded({
  assessment,
  employeeId,
  quarterId,
}: {
  assessment: Assessment;
  employeeId: string | 'me';
  quarterId: string;
}) {
  const save = useSaveSelf(employeeId, quarterId, assessment.id);
  const saveReview = useSaveReviewDraft(employeeId, quarterId, assessment.id);
  const submit = useSubmitStage(employeeId, quarterId, assessment.id);

  const reviewStage = assessment.myPendingStage;
  const isSelfMode = assessment.canEditSelf;
  /**
   * The last stage of THIS quarter's chain, which is not always Management: in a quarter
   * HR configured to end at their own review, the final rating form is HR's. Same rule
   * the API finalises on, so the screen cannot offer a form the submit ignores.
   */
  const isFinalStage = reviewStage !== null && nextStage(reviewStage, assessment.chain) === null;
  /**
   * The employee is being asked to answer their manager's rating.
   *
   * Kept out of `activeStage` below: acceptance fills in no column and submits no
   * ratings, so treating it as an active rating stage would draw an empty "Your
   * Acceptance" box beside every KRA and put a Submit button over an empty form.
   */
  const atAcceptance = reviewStage === 'EMPLOYEE_ACCEPTANCE';
  /** Who this lands on next, so the submit confirmation can name them. */
  const nextStageLabel = reviewStage
    ? (() => {
        const next = nextStage(reviewStage, assessment.chain);
        return next ? STAGE_LABELS[next] : null;
      })()
    : null;

  // No Summary or Challenges box on the form any more - the case for each KRA is
  // made in that KRA's own column. Whatever is stored still goes back untouched, so
  // dropping the inputs never wipes what an employee already wrote.
  const achievements = assessment.achievements ?? '';
  const challenges = assessment.challenges ?? '';
  const [feedback, setFeedback] = React.useState('');
  const [comments, setComments] = React.useState('');
  const [promotion, setPromotion] = React.useState(assessment.promotionRecommended);
  const [bonus, setBonus] = React.useState(assessment.bonusRecommended);
  const [finalRating, setFinalRating] = React.useState<number | ''>('');
  // Seeded from what is already stored, so reopening the form shows the standing
  // judgement rather than a blank that would read as "nobody has decided".
  const [potentialRating, setPotentialRating] = React.useState<number | ''>(
    assessment.potentialRating ?? '',
  );
  const [returning, setReturning] = React.useState(false);
  const [reopening, setReopening] = React.useState(false);
  const [declining, setDeclining] = React.useState(false);
  const [declineNote, setDeclineNote] = React.useState('');

  const accept = useAcceptRating(employeeId, quarterId, assessment.id);
  const decline = useDeclineRating(employeeId, quarterId, assessment.id);

  async function onAccept() {
    try {
      await accept.mutateAsync({ note: null });
      toast.success('Rating accepted', {
        description: 'Settled for this quarter. It has gone to HR for review.',
      });
    } catch (e) {
      toast.error('Could not accept', { description: err(e) });
    }
  }

  async function onDecline() {
    try {
      await decline.mutateAsync({ note: declineNote.trim() });
      setDeclining(false);
      setDeclineNote('');
      toast.success('Sent back to your manager', {
        description: 'They have been told why and will rate it again.',
      });
    } catch (e) {
      toast.error('Could not send it back', { description: err(e) });
    }
  }

  // Ratings for whichever stage this viewer is filling in.
  const activeStage: AssessmentStage | null = isSelfMode
    ? 'SELF'
    : atAcceptance
      ? null
      : reviewStage;
  const [ratings, setRatings] = React.useState<Record<string, number | ''>>(() =>
    Object.fromEntries(
      assessment.kras.map((kra) => [
        kra.kraId,
        seedRating(kra, isSelfMode ? 'SELF' : reviewStage),
      ]),
    ),
  );
  // Per-KRA notes, kept separately from the stage-wide `comments` above: this one is
  // "why this KRA got this band", which is what a later reviewer actually argues with.
  const [kraComments, setKraComments] = React.useState<Record<string, string>>(() =>
    Object.fromEntries(
      assessment.kras.map((kra) => [
        kra.kraId,
        kra.ratings.find((r) => r.stage === (isSelfMode ? 'SELF' : reviewStage))?.comment ?? '',
      ]),
    ),
  );

  /**
   * Whether the stage being filled in rates each KRA. Self and Manager do; HR and
   * Management read the manager's result and give one overall rating instead, so for
   * them nothing below about bands, comments or per-KRA drafts applies.
   */
  const kraStage = activeStage !== null && ratesKras(activeStage);

  /**
   * A band and the reason for it - the same rule `submitStage` refuses on, read from
   * the shared helper so the disabled button and the API's rejection cannot disagree
   * about what is still missing.
   */
  const { unrated, uncommented } = incompleteKraAnswers(
    assessment.kras.map((k) => k.kraId),
    assessment.kras.map((k) => ({
      kraId: k.kraId,
      rating: typeof ratings[k.kraId] === 'number' ? (ratings[k.kraId] as number) : null,
      comment: kraComments[k.kraId] ?? null,
    })),
  );
  const ratedCount = assessment.kras.length - unrated.length;
  // An overall rating is optional at HR and Management - blank keeps the one standing.
  const canSubmit = kraStage
    ? assessment.kras.length > 0 && unrated.length === 0 && uncommented.length === 0
    : activeStage !== null;

  // `override` exists because the autosave below fires from inside the onChange that
  // sets the rating, one render before state has caught up - without it every autosave
  // would persist the band picked before the one just clicked.
  function ratingPayload(
    override?: Record<string, number | ''>,
    overrideComments?: Record<string, string>,
  ): KraRatingInput[] {
    const source = override ?? ratings;
    const notes = overrideComments ?? kraComments;
    return (
      assessment.kras
        .map((k) => ({
          kraId: k.kraId,
          rating: typeof source[k.kraId] === 'number' ? (source[k.kraId] as number) : null,
          comment: notes[k.kraId]?.trim() || null,
        }))
        /**
         * A row for anything the employee has written, band or not.
         *
         * This used to drop every KRA without a band, which is what made a comment
         * typed before rating vanish on save - and evidence hangs off this row, so
         * there was nowhere to attach a document either. Sending nothing at all for an
         * untouched KRA still matters: it keeps a blank assessment from creating a row
         * per KRA the moment somebody opens it.
         */
        .filter((r) => r.rating !== null || r.comment !== null)
    );
  }

  /**
   * KRAs under their goal, so the title and weightage are said once instead of
   * restated on every row beneath them. The weightage has to stay somewhere - it is
   * why one KRA moves the overall more than another - but three identical lines in a
   * row is how it stopped being read.
   */
  const goalHeading = useGoalHeading();
  const goalGroups = React.useMemo(() => {
    const byGoal = new Map<
      string,
      {
        id: string;
        title: string;
        description: string | null;
        weightage: number;
        kras: AssessmentKra[];
      }
    >();
    for (const kra of assessment.kras) {
      const group = byGoal.get(kra.goalId) ?? {
        id: kra.goalId,
        title: kra.goalTitle,
        description: kra.goalDescription,
        weightage: kra.goalWeightage,
        kras: [],
      };
      group.kras.push(kra);
      byGoal.set(kra.goalId, group);
    }
    return [...byGoal.values()];
  }, [assessment.kras]);

  /**
   * Which stages get a column on each KRA: the two that rate them. HR and Management
   * appear only in the overalls above the list, never beside a KRA.
   */
  const columnStages = KRA_RATING_STAGES;

  /**
   * Which stages get an overall figure in the header: every rating stage this
   * quarter's chain runs. Self and Manager's are computed from their KRAs; HR and
   * Management's are the one number each of them types.
   */
  const overallStages = RATING_STAGES.filter((stage) => chainIncludes(stage, assessment.chain));

  /**
   * `silent` is the autosave path: picking a band or writing a note saves immediately,
   * which is what creates the KraRating row that evidence attaches to. Without it the
   * employee had to know to press Save before the upload box would appear, which is
   * an implementation detail nobody should have to learn. Errors still surface.
   *
   * The warning about comments that could not be saved is gone with the reason for it:
   * a comment no longer needs a band to survive.
   */
  async function saveDraft(opts?: {
    silent?: boolean;
    ratings?: Record<string, number | ''>;
    comments?: Record<string, string>;
  }) {
    const ratings = ratingPayload(opts?.ratings, opts?.comments);
    try {
      // The employee's draft also carries their narrative; a reviewer's is the KRA rows
      // alone. Two routes rather than one widened endpoint, so `achievements` and
      // `challenges` stay the employee's and nobody else can write them.
      if (isSelfMode) {
        await save.mutateAsync({ achievements, challenges, ratings });
      } else {
        await saveReview.mutateAsync({ ratings });
      }
      if (opts?.silent) return;
      toast.success('Draft saved');
    } catch (e) {
      toast.error('Could not save', { description: err(e) });
    }
  }

  async function submitStage() {
    if (!activeStage) return;
    try {
      await submit.mutateAsync({
        stage: activeStage,
        ratings: kraStage ? ratingPayload() : [],
        feedback: feedback || null,
        comments: comments || null,
        // The one overall figure HR and Management each give. Not the manager's -
        // theirs is computed from their KRA bands on the API.
        ...(!kraStage ? { overallRating: finalRating === '' ? null : finalRating } : {}),
        ...(isFinalStage
          ? {
              potentialRating: potentialRating === '' ? null : potentialRating,
              promotionRecommended: promotion,
              bonusRecommended: bonus,
            }
          : {}),
      });
      toast.success(
        activeStage === 'SELF'
          ? 'Submitted — your answers are now locked'
          : `${STAGE_LABELS[activeStage]} submitted`,
      );
    } catch (e) {
      toast.error('Could not submit', { description: err(e) });
    }
  }

  /**
   * What the bands on screen would produce, shown live so whoever is rating sees the
   * consequence before committing it.
   *
   * Null until every KRA has one. Averaging the rated few divides by only those goals'
   * weight, so two of eighteen KRAs read as a settled "6 · B" - a number that was
   * nobody's rating and lurched with every pick. This is the same rule the API applies
   * on submission, where `findUnratedGoals` rejects a partial set rather than
   * computing an overall from half of it.
   */
  const previewRating = React.useMemo(() => {
    if (assessment.kras.length === 0) return null;
    if (assessment.kras.some((kra) => typeof ratings[kra.kraId] !== 'number')) return null;

    // meanRating/weightedOverall, not arithmetic written again here: this used to be a
    // hand-rolled plain mean per goal, which is now the wrong answer for a goal whose
    // KRAs carry their own shares - and a preview that disagrees with the rating the
    // API records is worse than no preview.
    return weightedOverall(
      goalGroups.flatMap((goal) => {
        const mean = meanRating(
          goal.kras.flatMap((kra) => {
            const rating = ratings[kra.kraId];
            return typeof rating === 'number' ? [{ weightage: kra.weightage, rating }] : [];
          }),
        );
        return mean === null ? [] : [{ weightage: goal.weightage, rating: mean }];
      }),
    );
  }, [assessment.kras, goalGroups, ratings]);

  const readOnly = !isSelfMode && !reviewStage;
  /** Their own record, as opposed to a reviewer looking at someone else's. */
  const ownRecord = employeeId === 'me';

  /**
   * At most one exception pill, in order of what the reader most needs to know.
   * Overdue outranks the others because it is the only one nobody has acted on yet;
   * a returned assessment IS reopened, so naming both would say the same thing twice.
   */
  const exception: 'returned' | 'reopened' | 'overdue' | null = assessment.overdue
    ? 'overdue'
    : assessment.returnedAt
      ? 'returned'
      : assessment.reopenActive
        ? 'reopened'
        : null;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle className="flex flex-wrap items-center gap-2">
                {assessment.quarterLabel} assessment
                <Badge variant={assessmentStatusVariant(assessment.status)} className={PILL_CLASS}>
                  {assessment.status.replace(/_/g, ' ').toLowerCase()}
                </Badge>
                {/* Beside the status, not instead of it. "SELF IN PROGRESS" is true
                    of a returned assessment and of one nobody has started, and only
                    this second pill tells those two apart. */}
                {exception && (
                  <Badge variant={assessmentExceptionVariant(exception)} className={PILL_CLASS}>
                    {ASSESSMENT_EXCEPTION_LABELS[exception]}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{assessment.employeeName}</CardDescription>
            </div>

            <div className="flex items-start gap-3">
              {/* In the header rather than the action row: a COMPLETED assessment has
                  no active stage, so that row is not rendered — and completed is
                  exactly when HR most often needs this. */}
              {assessment.canReopen && (
                <Button hint={"Reopen this assessment so it can be changed"} variant="outline" size="sm" className="gap-2" onClick={() => setReopening(true)}>
                  <LockOpen className="size-4" />
                  Reopen
                </Button>
              )}

              {assessment.finalRating !== null && assessment.finalGrade && (
                <div className="text-right">
                  <div className="pms-num text-3xl font-bold">{assessment.finalRating}</div>
                  <Badge variant={gradeVariant(assessment.finalGrade)} className={PILL_CLASS}>
                    {assessment.finalGrade}
                  </Badge>
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Returned first, above the lock notice: it is the one that tells the
                employee there is something for them to do right now. */}
            {assessment.returnedAt && assessment.returnNote && (
              <Alert variant="warning">
                <Undo2 className="size-4" />
                <AlertDescription className="space-y-1 text-sm">
                  <p className="font-medium">Sent back for correction</p>
                  <p>{assessment.returnNote}</p>
                  {assessment.reopenedUntil && (
                    <p className="text-xs">
                      Editable until {new Date(assessment.reopenedUntil).toLocaleDateString()}.
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* The manager's brief for the rework, and the employee's own record that
                they objected. Above the reopen notice because it is the only one that
                names something somebody has to change. */}
            {assessment.ratingDeclinedNote && (
              <Alert variant="warning">
                <Undo2 className="size-4" />
                <AlertDescription className="space-y-1 text-sm">
                  <p className="font-medium">
                    {ownRecord
                      ? 'You sent this back to your manager'
                      : `${assessment.employeeName} declined this rating`}
                  </p>
                  <p>{assessment.ratingDeclinedNote}</p>
                  {!ownRecord && (
                    <p className="text-xs">Update your ratings and submit the review again.</p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Amber even though it helps the employee — "HR intervened by hand on
                this one record" is what every later reviewer should notice. */}
            {assessment.reopenActive && !assessment.returnedAt && assessment.reopenedUntil && (
              <Alert variant="warning">
                <LockOpen className="size-4" />
                <AlertDescription className="text-sm">
                  HR reopened this assessment. It is editable until{' '}
                  {new Date(assessment.reopenedUntil).toLocaleDateString()}.
                </AlertDescription>
              </Alert>
            )}

            {assessment.overdue && (
              <Alert variant="destructive">
                <AlertDescription className="text-sm">
                  The {assessment.quarterLabel} window closed with nothing submitted. The reporting
                  manager has been notified; HR can reopen this assessment or force the window open.
                </AlertDescription>
              </Alert>
            )}

            {assessment.lockedAt && isSelfMode === false && (
              <Alert>
                <Lock className="size-4" />
                <AlertDescription className="text-sm">
                  Submitted {new Date(assessment.lockedAt).toLocaleDateString()} and locked. The
                  employee&rsquo;s own answers can no longer change.
                </AlertDescription>
              </Alert>
            )}


            {assessment.frozen && (
              <Alert>
                <Lock className="size-4" />
                <AlertDescription className="text-sm">
                  HR has frozen {assessment.quarterLabel}. This assessment is settled and no stage
                  can be submitted or changed.
                </AlertDescription>
              </Alert>
            )}

            {!assessment.frozen && !assessment.windowOpen && assessment.status === 'NOT_STARTED' && (
              <Alert>
                <AlertDescription className="text-sm">
                  The self-assessment window for this quarter is not open. HR can open it manually
                  if this quarter needs to be completed late.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle>Key result areas</CardTitle>
              <CardDescription>
                Each KRA is rated on the five-band scale.{' '}
                {ownRecord
                  ? 'Development goals are excluded from the overall rating.'
                  : 'The overall rating is weighted by each goal’s weightage; development goals carry 0% and are excluded.'}
              </CardDescription>
            </div>

            {/* The overalls side by side: the gap between them is the conversation
                this page exists to start, and it should not need scrolling to find. */}
            <div className="flex flex-wrap gap-6">
              {overallStages.map((stage) => {
                const submitted = assessment.stages.find((s) => s.stage === stage);
                const active = stage === activeStage;
                // Live for the stage being filled in, so a reviewer sees where their
                // bands - or their one typed figure - land before committing; recorded
                // for everyone else.
                const value =
                  submitted?.overallRating ??
                  (active ? (kraStage ? previewRating : finalRating === '' ? null : finalRating) : null);
                // Never a bare number, and never a number that is not the whole answer:
                // a KRA stage being filled in reports its progress until every KRA is
                // rated, because "6 of 18 done" is the fact, not a partial average.
                const caption = submitted
                  ? value === null
                    ? 'submitted, no rating'
                    : `${toGrade(value)} · submitted`
                  : active
                    ? kraStage
                      ? `${value === null ? '' : `${toGrade(value)} · `}${ratedCount} of ${assessment.kras.length} rated`
                      : value === null
                        ? 'blank keeps the rating standing'
                        : `${toGrade(value)} · not submitted`
                    : 'not submitted';
                return (
                  <div key={stage}>
                    <p className="text-xs text-muted-foreground">{STAGE_LABELS[stage]}</p>
                    <p className="pms-num text-2xl font-bold">{value ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">{caption}</p>
                  </div>
                );
              })}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {assessment.kras.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No accepted organization goals for this cycle yet, so there is nothing to assess.
              </p>
            )}

            {goalGroups.map((goal) => (
              <div key={goal.id} className="space-y-3">
                <div className="flex items-baseline justify-between gap-2 border-b pb-1">
                  <p className="text-sm font-semibold">{goalHeading(goal).heading}</p>
                  {/* Weightage is the reviewer's tool - it is how they see which KRA
                      moves the number - and it is not shown on someone's own record. */}
                  {!ownRecord && (
                    <p className="pms-num shrink-0 text-xs text-muted-foreground">
                      {goal.weightage}% of the rating
                    </p>
                  )}
                </div>

                <GoalProgressLog
                  goalId={goal.id}
                  updates={assessment.goalUpdates.filter((u) => u.goalId === goal.id)}
                />

                {/* One KRA per collapsed row. Eighteen of these open at once is a page
                    nobody can find their place in, so the row carries the bands already
                    given and opens only when someone means to work on it. */}
                {goal.kras.map((kra) => (
                  <Collapsible key={kra.kraId} className="rounded-md border">
                    {/* The Save button sits beside the trigger, not inside it — a
                        button nested in a button is invalid HTML and the click would
                        toggle the row as well as save. */}
                    <div className="flex items-center">
                    <CollapsibleTrigger data-guide="kra-row" className="flex min-w-0 flex-1 items-center gap-3 p-3 text-left hover:bg-muted/50 [&[data-state=open]_.chevron]:rotate-180">
                      <span className="min-w-0 flex-1 font-medium">{kra.name}</span>

                      {/* Only where it changes something: with every KRA under this goal
                          at 0 they count equally, and a column of "0%" would say the
                          opposite of what it means. Reviewers only, same as the goal's
                          own weightage above. */}
                      {!ownRecord && goal.kras.some((k) => k.weightage > 0) && (
                        <span className="pms-num shrink-0 text-xs text-muted-foreground">
                          {kra.weightage}%
                        </span>
                      )}

                      <span className="flex shrink-0 flex-wrap justify-end gap-x-3 text-xs text-muted-foreground">
                        {columnStages.map((stage) => {
                          // The stage being filled in reads from what is on screen, so a
                          // band picked and collapsed again still shows on the row.
                          const live =
                            stage === activeStage && typeof ratings[kra.kraId] === 'number'
                              ? (ratings[kra.kraId] as number)
                              : (kra.ratings.find((r) => r.stage === stage)?.rating ?? null);
                          return (
                            <span key={stage}>
                              {SHORT_STAGE[stage]}{' '}
                              <span
                                className={cn(
                                  'font-semibold',
                                  live !== null && 'text-foreground',
                                )}
                              >
                                {live === null ? '—' : toGrade(live)}
                              </span>
                            </span>
                          );
                        })}
                      </span>

                      <ChevronDown className="chevron size-4 shrink-0 text-muted-foreground transition-transform" />
                    </CollapsibleTrigger>

                    {/* Saves the whole draft, same as the button at the foot of the
                        page — it is here so nobody has to scroll past eighteen rows
                        to reach one. */}
                    {kraStage && !readOnly && (
                      <Button hint={"Save these ratings and comments"}
                        data-guide="kra-save"
                        variant="ghost"
                        size="sm"
                        className="mr-2 shrink-0 gap-1.5"
                        disabled={save.isPending || saveReview.isPending}
                        onClick={() => void saveDraft()}
                      >
                        <Save className="size-4" />
                        {save.isPending || saveReview.isPending ? 'Saving…' : 'Save'}
                      </Button>
                    )}
                    </div>

                    {/* One column per stage, side by side, because the manager's job here
                        is to answer the employee's own words - "why this band, and what is
                        the evidence" - not to rate in a vacuum and meet them later. */}
                    <CollapsibleContent className="grid gap-3 border-t p-3 sm:grid-cols-2">
                      {columnStages.map((stage) => (
                        <KraStageColumn
                          key={stage}
                          kra={kra}
                          stage={stage}
                          editable={stage === activeStage && !readOnly}
                          rating={ratings[kra.kraId] ?? ''}
                          comment={kraComments[kra.kraId] ?? ''}
                          onRating={(v) => {
                            const next = { ...ratings, [kra.kraId]: v };
                            setRatings(next);
                            // Both sides autosave now: picking a band creates the row
                            // this stage's evidence attaches to, reviewer or employee.
                            void saveDraft({ silent: true, ratings: next });
                          }}
                          onComment={(v) =>
                            setKraComments((prev) => ({ ...prev, [kra.kraId]: v }))
                          }
                          // `kraComments` is passed explicitly for the same reason the
                          // rating override exists: blur fires before state has caught
                          // up, so reading it here would persist the previous keystroke.
                          onCommentBlur={() =>
                            void saveDraft({ silent: true, comments: kraComments })
                          }
                          onSaveDraft={() => void saveDraft()}
                          savePending={save.isPending || saveReview.isPending}
                        />
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            ))}

            {/*
              The employee's answer to their manager's rating.

              In the same place the Submit row sits for every other stage, and after the
              KRAs rather than above them: the decision is "having read these bands, do I
              agree", so the buttons belong at the end of the reading.
            */}
            {atAcceptance && (
              <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-4">
                <div>
                  <p className="text-sm font-semibold">
                    Your manager has completed your review
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Accepting settles your rating for {assessment.quarterLabel} and sends it on
                    to HR. If something is wrong, send it back with a reason and your manager
                    will rate it again.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <ConfirmButton
                    className="gap-2"
                    disabled={accept.isPending || decline.isPending}
                    title={`Accept your ${assessment.quarterLabel} rating?`}
                    description="Your manager's rating is settled for this quarter and goes on to HR. You cannot take this back — if you disagree with any of it, send it back instead and they will rate it again."
                    confirmLabel="Accept my rating"
                    pending={accept.isPending}
                    onConfirm={() => void onAccept()}
                  >
                    <Check className="size-4" />
                    {accept.isPending ? 'Accepting…' : 'Accept my rating'}
                  </ConfirmButton>
                  {/* No confirm on Decline: it already opens a dialog that requires a
                      reason, which is its own deliberate step. */}
                  <Button hint={"Send the assessment back to the previous stage, with a reason"}
                    variant="outline"
                    className="gap-2"
                    disabled={accept.isPending || decline.isPending}
                    onClick={() => setDeclining(true)}
                  >
                    <Undo2 className="size-4" />
                    Decline and send back
                  </Button>
                </div>
              </div>
            )}

            {activeStage && assessment.kras.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-muted p-3">
                <div className="text-sm">
                  {kraStage ? (
                    <>
                      <span className="text-muted-foreground">Rated </span>
                      <span className="pms-num font-semibold">
                        {ratedCount}/{assessment.kras.length}
                      </span>
                      {/* Why Submit is disabled, said here rather than left to be guessed
                          at from a greyed-out button. */}
                      {uncommented.length > 0 && (
                        <span className="text-muted-foreground">
                          {' '}
                          · <span className="pms-num">{uncommented.length}</span> still need a
                          comment
                        </span>
                      )}
                      {previewRating !== null && (
                        <>
                          <span className="text-muted-foreground"> · weighted </span>
                          <span className="pms-num font-semibold">{previewRating}</span>
                          <span className="text-muted-foreground"> ({toGrade(previewRating)})</span>
                        </>
                      )}
                    </>
                  ) : (
                    // Nothing to count here: this stage gives its one figure in the
                    // card below, and Submit is never gated on it.
                    <span className="text-muted-foreground">
                      Your overall rating goes in the {STAGE_LABELS[activeStage]} card below.
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  {isSelfMode && (
                    <Button hint={"Keep your answers without submitting — you can come back to them"} variant="outline" onClick={() => void saveDraft({})} disabled={save.isPending}>
                      Save draft
                    </Button>
                  )}
                  {/* Sits beside Submit, not in a menu: "this needs work" is as
                      ordinary an outcome of a review as signing it off. */}
                  {assessment.canReturn && (
                    <Button hint={"Send it back to the employee to correct"} variant="outline" className="gap-2" onClick={() => setReturning(true)}>
                      <Undo2 className="size-4" />
                      Return for correction
                    </Button>
                  )}
                  <ConfirmButton
                    className="gap-2"
                    disabled={!canSubmit || submit.isPending}
                    title={
                      isSelfMode
                        ? `Submit your ${assessment.quarterLabel} self assessment?`
                        : `Submit the ${STAGE_LABELS[activeStage]}?`
                    }
                    description={
                      isSelfMode
                        ? 'Your ratings, comments and evidence lock, and your manager is asked to review them. You cannot edit any of it afterwards unless a reviewer sends it back to you.'
                        : `Your ratings lock and the assessment moves to ${
                            nextStageLabel ?? 'the next stage'
                          }. You cannot change them afterwards.`
                    }
                    confirmLabel={isSelfMode ? 'Submit and lock' : 'Submit'}
                    pending={submit.isPending}
                    onConfirm={() => void submitStage()}
                  >
                    <Send className="size-4" />
                    {isSelfMode ? 'Submit and lock' : `Submit ${STAGE_LABELS[activeStage]}`}
                  </ConfirmButton>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sits in the main column with the quarter's content, not the 280px status
            rail. Renders nothing when they showcased nothing. */}
        <DevConnectCard
          employeeId={assessment.employeeId}
          quarterId={assessment.quarterId}
          quarterLabel={assessment.quarterLabel}
        />

        {/* Acceptance writes no feedback and no comments of its own - its answer is a
            button and, if declined, a reason. */}
        {reviewStage && !atAcceptance && (
          <Card>
            <CardHeader>
              <CardTitle>{STAGE_LABELS[reviewStage]}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="feedback">Feedback</Label>
                <Textarea
                  id="feedback"
                  rows={3}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="comments">Comments</Label>
                <Textarea
                  id="comments"
                  rows={2}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </div>

              {/* HR and Management each give ONE overall rating for the quarter, not a
                  band per KRA - the manager's column already did that work. The last of
                  them also scores potential and makes the recommendations. */}
              {!kraStage && (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label>{isFinalStage ? 'Final rating' : 'Overall rating'}</Label>
                      <p className="text-xs text-muted-foreground">
                        Your one rating for the quarter. Leave blank to keep the rating standing
                        {isFinalStage ? ' — it becomes the final one.' : '.'}
                      </p>
                    </div>
                    <RatingInput
                      label={isFinalStage ? 'Final rating' : 'Overall rating'}
                      value={finalRating}
                      onChange={setFinalRating}
                    />
                  </div>
                </div>
              )}

              {isFinalStage && (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label>Potential</Label>
                      <p className="text-xs text-muted-foreground">
                        The 9-box y axis — how far you think they can go, which is a separate
                        question from how this quarter went. Blank leaves them placed by their
                        rating trend.
                      </p>
                    </div>
                    <RatingInput
                      label="Potential"
                      value={potentialRating}
                      onChange={setPotentialRating}
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={promotion}
                      onChange={(e) => setPromotion(e.target.checked)}
                    />
                    Recommend for promotion
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={bonus}
                      onChange={(e) => setBonus(e.target.checked)}
                    />
                    Recommend for bonus
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Last, after everything with a KRA: proof for one KRA belongs on that KRA,
            where the reviewer questioning it is looking. This is the remainder. */}
        <EvidencePanel
          entityType="ASSESSMENT"
          entityId={assessment.id}
          canUpload={isSelfMode || Boolean(reviewStage)}
          description="Evidence about the quarter as a whole. Proof for one KRA belongs on that KRA above."
        />
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review chain</CardTitle>
          </CardHeader>
          <CardContent>
            <StageTracker
              chain={assessment.chain}
              completed={assessment.completedStages}
              active={assessment.stageAwaiting}
              overdue={assessment.managerOverdue}
              actors={Object.fromEntries(
                assessment.stages.map((s) => [
                  s.stage,
                  `${s.actorName} · ${new Date(s.submittedAt).toLocaleDateString()}`,
                ]),
              )}
            />

            {assessment.managerDueAt && assessment.status === 'AWAITING_MANAGER' && (
              <p
                className={cn(
                  'mt-4 text-xs',
                  assessment.managerOverdue ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                Manager review due{' '}
                {formatPlainDate(new Date(assessment.managerDueAt).toISOString().slice(0, 10))}
                {assessment.managerOverdue ? ' — overdue' : ''}
              </p>
            )}
          </CardContent>
        </Card>

        {assessment.stages.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Feedback so far</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {assessment.stages.map((s) => (
                <div key={s.stage} className="space-y-1">
                  <p className="text-xs font-semibold">{STAGE_LABELS[s.stage]}</p>
                  <p className="text-xs text-muted-foreground">{s.actorName}</p>
                  {s.feedback && <p className="text-sm">{s.feedback}</p>}
                  {s.comments && <p className="text-sm text-muted-foreground">{s.comments}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <ReturnDialog
        open={returning}
        onClose={() => setReturning(false)}
        employeeName={assessment.employeeName}
        employeeId={employeeId}
        quarterId={quarterId}
        assessmentId={assessment.id}
      />
      <DeclineDialog
        open={declining}
        onClose={() => setDeclining(false)}
        note={declineNote}
        onNote={setDeclineNote}
        onConfirm={() => void onDecline()}
        pending={decline.isPending}
      />
      <ReopenDialog
        open={reopening}
        onClose={() => setReopening(false)}
        employeeName={assessment.employeeName}
        employeeId={employeeId}
        quarterId={quarterId}
        assessmentId={assessment.id}
      />
    </div>
  );
}

/**
 * Declining a manager's rating.
 *
 * The reason is required and the button stays disabled without it — the same rule the
 * API enforces. The manager is being asked to do the review again, and "I disagree"
 * with nothing after it is rework with no brief.
 *
 * Deliberately not the same dialog as ReturnDialog even though both send something
 * back with a note: that one unlocks the employee's answers and clears every review
 * recorded so far, which is the opposite of what this does.
 */
function DeclineDialog({
  open,
  onClose,
  note,
  onNote,
  onConfirm,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  note: string;
  onNote: (v: string) => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send this back to your manager</DialogTitle>
          <DialogDescription>
            Your own answers stay as you submitted them — only the manager&rsquo;s ratings go
            back. They will see your reason, rate it again, and it will come back to you.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={4}
          value={note}
          onChange={(e) => onNote(e.target.value)}
          maxLength={2000}
          aria-label="Why you disagree"
          placeholder="Which ratings do you disagree with, and why?"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button hint={"Send it back to the previous stage, with your reason"} disabled={note.trim().length === 0 || pending} onClick={onConfirm}>
            {pending ? 'Sending…' : 'Send back'}
          </Button>
          {note.trim().length === 0 && <span className="self-center text-xs text-muted-foreground">Add a note first</span>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Return for correction.
 *
 * The note is required and the button stays disabled without it — the same rule the
 * API enforces. "Sent back" with no reason is the employee's quarter reopened and
 * nothing to act on.
 */
function ReturnDialog({
  open,
  onClose,
  employeeName,
  employeeId,
  quarterId,
  assessmentId,
}: {
  open: boolean;
  onClose: () => void;
  employeeName: string;
  employeeId: string | 'me';
  quarterId: string;
  assessmentId: string;
}) {
  const mutation = useReturnForCorrection(employeeId, quarterId, assessmentId);
  const [note, setNote] = React.useState('');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send back to {employeeName}</DialogTitle>
          <DialogDescription>
            Their answers unlock and they get 3 days to resubmit. Every review recorded so
            far is cleared, because what those reviewers signed off is about to change.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What needs correcting?"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button hint={"Return it to the employee to correct"}
            disabled={note.trim().length === 0 || mutation.isPending}
            onClick={() =>
              mutation.mutate(
                { note: note.trim() },
                {
                  onError: (e) => toast.error('Could not return it', { description: e.message }),
                  onSuccess: () => {
                    toast.success(`Sent back to ${employeeName}`);
                    setNote('');
                    onClose();
                  },
                },
              )
            }
          >
            {mutation.isPending ? 'Sending…' : 'Send back'}
          </Button>
          {note.trim().length === 0 && <span className="self-center text-xs text-muted-foreground">Add a note first</span>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * HR reopens a locked assessment.
 *
 * A date input rather than a fixed grace period: the reasons HR reopens something —
 * long leave, a late joiner, a payroll deadline — do not share one duration. Sent as
 * an end-of-day instant so the employee has the whole day named.
 */
function ReopenDialog({
  open,
  onClose,
  employeeName,
  employeeId,
  quarterId,
  assessmentId,
}: {
  open: boolean;
  onClose: () => void;
  employeeName: string;
  employeeId: string | 'me';
  quarterId: string;
  assessmentId: string;
}) {
  const mutation = useReopenAssessment(employeeId, quarterId, assessmentId);
  const [until, setUntil] = React.useState(() =>
    new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10),
  );
  const [reason, setReason] = React.useState('');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen {employeeName}&rsquo;s assessment</DialogTitle>
          <DialogDescription>
            It unlocks for this one employee without moving the quarter&rsquo;s window for
            anyone else. The review chain restarts from their submission, and any final
            rating already recorded is cleared.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="reopen-until">Editable until</Label>
            <PlainDatePicker id="reopen-until" value={until} onChange={setUntil} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reopen-reason">Reason</Label>
            <Textarea
              id="reopen-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being reopened?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button hint={"Reopen it until the chosen date"}
            disabled={reason.trim().length === 0 || until === '' || mutation.isPending}
            onClick={() =>
              mutation.mutate(
                // End of the chosen day, so "until the 10th" includes the 10th.
                { until: new Date(`${until}T23:59:59.000Z`).toISOString(), reason: reason.trim() },
                {
                  onError: (e) => toast.error('Could not reopen it', { description: e.message }),
                  onSuccess: () => {
                    toast.success(`Reopened for ${employeeName}`);
                    setReason('');
                    onClose();
                  },
                },
              )
            }
          >
            {mutation.isPending ? 'Reopening…' : 'Reopen'}
          </Button>
          {(reason.trim().length === 0 || until === '') && <span className="self-center text-xs text-muted-foreground">{reason.trim().length === 0 ? 'Give a reason' : 'Choose the date it stays open until'}</span>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
